import PptxGenJS from 'pptxgenjs';
import { QUESTION_TYPES } from '@api/instant_survey';
import {
  createContSumChartSlide,
  createGridChartSlide,
  createGridTableSlide,
  createMaxDiffChartSlide,
  createMaxDiffTableSlide,
  createMetadataSlide,
  createRankChartSlide,
  createRankTableSlide,
  createSurveyChartSlide,
  createTextSlide,
  createTitleSlide,
} from '../../../../../components/Builder/components/Toolbar/components/Download/components/DownloadPowerPoint/utilities';
import {
  getNumericKeyFindingsForPowerPoint,
  getTextSummariesForPowerPoint,
} from './keyFindingsFilter';
import { SEARCH_PARAM_KEYS } from '../../../utilities/constants';

/**
 * Cleans and processes question title text
 * @param {String} title
 * @returns {String}
 */
export const cleanQuestionTitle = (title) => {
  if (!title) return '';
  return title?.replace(/\\"/g, '"').replace(/\s+/g, ' ').trim();
};

/**
 * Exports survey responses and key findings to a PowerPoint presentation
 * @param {Object} options - Export options
 * @param {Object} options.details - Survey details
 * @param {Object} options.displayAs - Display format object with label and value properties
 * @param {Object} options.globalFilters - Global filter options
 * @param {Object} options.insightsDocument - Insights document data
 * @param {Object} options.keyFindingsMap - Map of question IDs to key findings
 * @param {String} options.publicToken - Public token for the survey
 * @param {Object} options.questionsRef - Reference to survey questions
 * @param {Object} options.responseData - Survey response data
 * @param {URLSearchParams} options.searchParams - URL search parameters
 * @param {Object} options.selectedCompareBy - Selected comparison option
 * @param {Object} options.survey - Survey object
 * @param {Array} options.surveyQuestions - Array of survey questions
 * @param {String} options.surveyId - Survey ID
 */
export const exportPowerPoint = async ({
  details,
  displayAs,
  globalFilters,
  insightsDocument,
  keyFindingsMap = {},
  questionsRef,
  responseData,
  searchParams,
  selectedCompareBy,
  survey,
  surveyQuestions,
}) => {
  const surveyTitle = survey?.title || insightsDocument.title;
  const surveyResponsesUrl = window.location.href;
  const surveyResponsesArray = responseData ? Object.values(responseData).filter(Boolean) : [];
  const surveyReportType = 'Survey Results';

  const {
    [SEARCH_PARAM_KEYS.QUESTION_OPTION]: selectedOptions,
    [SEARCH_PARAM_KEYS.QUOTA_GROUP]: selectedGroups,
    ...otherParams
  } = Object.fromEntries(searchParams);

  const areFiltersApplied =
    globalFilters?.demographicFilters?.some(({ id }) => otherParams[id]?.split(',')?.length > 0) ||
    selectedOptions?.split(',')?.length > 0 ||
    selectedGroups?.split(',')?.length > 0;

  const pptx = new PptxGenJS();

  // Add title slide
  createTitleSlide({
    pptx,
    reportName: surveyTitle,
    reportType: 'Verified Voices',
  });

  // Add metadata slide
  createMetadataSlide({
    details,
    globalFilters,
    insightsDocument,
    pptx,
    questionsRef,
    reportType: surveyReportType,
    reportUrl: surveyResponsesUrl,
    searchParams,
    selectedCompareBy,
    survey,
    surveyQuestions,
    surveyResponsesUrl,
    surveyTitle,
  });

  // Get displayAs value and compareBy object once for all question processing
  const displayAsValue = displayAs.value;

  // Process ALL questions in their original order to maintain question sequence
  for (const [index, question] of surveyQuestions.entries()) {
    const questionData = surveyResponsesArray.find(({ id }) => id === question.id);
    if (!questionData) continue;

    try {
      switch (question.type) {
        case QUESTION_TYPES.ESSAY.value:
          // Only create text slides when comparing by overall or quota group
          if (!['overall', 'quota_group'].includes(selectedCompareBy?.value)) {
            continue;
          }

          // Only include text summaries if no filters are applied, using exact UI logic
          const questionTextSummaries = getTextSummariesForPowerPoint({
            areFiltersApplied,
            question,
            selectedCompareBy,
            surveyResponseSummariesMap: { questionSummaries: keyFindingsMap.questionSummaries },
          });

          createTextSlide({
            compareBy: selectedCompareBy,
            pptx,
            question,
            questionNumber: index + 1,
            reportType: 'OPEN_END_QUESTION',
            reportUrl: surveyResponsesUrl,
            textSummaries: questionTextSummaries || [],
            title: surveyTitle,
          });
          break;

        case QUESTION_TYPES.RANK.value:
          createRankChartSlide({
            compareBy: selectedCompareBy,
            displayAs: displayAsValue,
            pptx,
            questionData,
            questionNumber: index + 1,
            questionTitle: question.title,
            reportType: surveyReportType,
            reportUrl: surveyResponsesUrl,
            title: surveyTitle,
          });
          createRankTableSlide({
            compareBy: selectedCompareBy,
            displayAs: displayAsValue,
            pptx,
            questionData,
            questionNumber: index + 1,
            questionTitle: question.title,
            reportType: surveyReportType,
            reportUrl: surveyResponsesUrl,
            title: surveyTitle,
          });
          break;

        case QUESTION_TYPES.CONT_SUM.value:
          createContSumChartSlide({
            compareBy: selectedCompareBy,
            displayAs: displayAsValue,
            pptx,
            questionData,
            questionNumber: index + 1,
            questionTitle: question.title,
            reportType: surveyReportType,
            reportUrl: surveyResponsesUrl,
            title: surveyTitle,
          });
          break;

        case QUESTION_TYPES.TABLE.value:
          createGridChartSlide({
            compareBy: selectedCompareBy,
            displayAs: displayAsValue,
            pptx,
            questionData,
            questionNumber: index + 1,
            questionTitle: question.title,
            reportDateRange: null,
            reportType: surveyReportType,
            reportUrl: surveyResponsesUrl,
            title: surveyTitle,
          });
          createGridTableSlide({
            compareBy: selectedCompareBy,
            displayAs: displayAsValue,
            pptx,
            questionData,
            questionNumber: index + 1,
            questionTitle: question.title,
            reportType: surveyReportType,
            reportUrl: surveyResponsesUrl,
            title: surveyTitle,
          });
          break;

        case 'MAXDIFF':
          createMaxDiffChartSlide({
            compareBy: selectedCompareBy,
            displayAs: displayAsValue,
            pptx,
            questionData,
            questionNumber: index + 1,
            questionTitle: question.title,
            reportType: surveyReportType,
            reportUrl: surveyResponsesUrl,
            title: surveyTitle,
          });

          createMaxDiffTableSlide({
            compareBy: selectedCompareBy,
            displayAs: displayAsValue,
            pptx,
            questionData,
            questionNumber: index + 1,
            questionTitle: question.title,
            reportType: surveyReportType,
            reportUrl: surveyResponsesUrl,
            title: surveyTitle,
          });
          break;

        default:
          // Get chart data
          const useCountData = displayAs?.value === 'count';
          let chartData;
          if (questionData.chartCount || questionData.chartPct) {
            chartData = useCountData ? questionData.chartCount : questionData.chartPct;
          } else if (questionData.results?.length) {
            chartData = {
              series: questionData.results,
              title: question.title,
            };
          }

          if (chartData?.series?.length) {
            // Get key findings for this question using the same logic as UI
            const keyFindings = getNumericKeyFindingsForPowerPoint({
              areFiltersApplied,
              question,
              selectedCompareBy,
              surveyResponseSummariesMap: { questionSummaries: keyFindingsMap.questionSummaries },
            });

            createSurveyChartSlide({
              chart: chartData,
              compareBy: selectedCompareBy,
              keyFindings,
              pptx,
              questionNumber: index + 1,
              questionTitle: question.title,
              reportType: surveyReportType,
              reportUrl: surveyResponsesUrl,
              title: surveyTitle,
            });
          }
          break;
      }
    } catch (error) {
      console.error(`Error processing ${question.type} question ${question.id}:`, error);
    }
  }

  // Generate the file
  await pptx.writeFile({ fileName: `${surveyTitle}.pptx` });
};
